import type { SubmitApiResponse, SubmitRequestBody, SubmitResult } from '../types';
import {
  logSubmitStart,
  logSubmitWarn,
  logSubmitPerfSuccess,
  logSubmitPerfFailure,
  logSubmitSuccess,
  logSubmitError,
} from '../logger/submitLogger';
import { trackSubmitLatency, trackSubmitError } from '../telemetry/submitTelemetry';
import { getErrorMessage, toSdkLoggableError } from '../../types/shared';

/**
 * Perform the HTTP POST request to submit or partially save survey answers.
 */
export async function submitSurveyResponse({
  url,
  requestBody,
}: {
  url: string;
  requestBody: SubmitRequestBody;
}): Promise<SubmitResult> {
  const startTime = performance.now();
  const requestSize = new Blob([JSON.stringify(requestBody)]).size;
  const requestSizeStr = `${(requestSize / 1024).toFixed(2)} KB`;
  logSubmitStart(url, requestBody);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });

    const responseData = (await response.json().catch(err => {
      logSubmitWarn('Failed to parse JSON response during submission', toSdkLoggableError(err));
      return null;
    })) as SubmitApiResponse | null;

    const durationMs = performance.now() - startTime;
    const responseSize = responseData ? new Blob([JSON.stringify(responseData)]).size : 0;
    const responseSizeStr = `${(responseSize / 1024).toFixed(2)} KB`;

    if (!response.ok) {
      logSubmitPerfFailure(durationMs, response.status, url, requestSizeStr);
      trackSubmitError(response.status, url, `Submit failed (${response.status}): ${JSON.stringify(responseData)}`);
      throw new Error(`Submit failed (${response.status}): ${JSON.stringify(responseData)}`);
    }

    const responseId = responseData?.responseId ?? '';
    logSubmitPerfSuccess(durationMs, response.status, url, requestSizeStr, responseSizeStr);

    trackSubmitLatency(durationMs, response.status, url, requestSizeStr, responseSizeStr);

    logSubmitSuccess(responseId);
    return { success: true, responseId };
  } catch (err: unknown) {
    const durationMs = performance.now() - startTime;
    logSubmitPerfFailure(durationMs, 'FAILED', url, requestSizeStr);
    const message = getErrorMessage(err);
    trackSubmitError('FAILED', url, message);
    logSubmitError('Failed to submit survey response', toSdkLoggableError(err));
    throw err;
  }
}
